import { IonIcon } from "@ionic/react";
import { useRef } from "react";
import { type FieldApi } from "@tanstack/react-form";
import { closeOutline, informationCircleOutline } from "ionicons/icons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { cn } from "@/lib/style/cva.config";
import { ReadOnlyBadge } from "./read-only-badge";

export type CustomFieldToggleProps = {
  label: string;
  description?: string;
  required?: boolean;
  /** #10a: render disabled/read-only (employee may not edit this field). */
  readOnly?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldApi<
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >;
};

export function CustomFieldToggle({
  label,
  description,
  required = false,
  readOnly = false,
  field,
}: CustomFieldToggleProps) {
  const hasError = field.state.meta.errors && field.state.meta.errors.length > 0;
  // Extract error message - handle both string and ZodError object formats
  const errorMessage = hasError
    ? typeof field.state.meta.errors[0] === "string"
      ? field.state.meta.errors[0]
      : field.state.meta.errors[0]?.message || String(field.state.meta.errors[0])
    : null;
  const infoModalRef = useRef<HTMLIonModalElement>(null);

  // Convert boolean to string for select value
  const selectValue =
    field.state.value === true ? "true" : field.state.value === false ? "false" : "";
  const hasValue = selectValue !== "";

  return (
    <div className="flex flex-col gap-2">
      {/* Label */}
      <div className="flex items-center gap-1.5">
        <label htmlFor={field.name} className="text-md font-semibold text-txt-primary">
          {label}
          {required && <span className="text-error-120"> *</span>}
        </label>
        <ReadOnlyBadge readOnly={readOnly} />
        {description && (
          <button
            type="button"
            onClick={() => infoModalRef.current?.present()}
            className="flex h-6 w-6 items-center justify-center"
          >
            <IonIcon icon={informationCircleOutline} className="text-xl text-txt-tertiary" />
          </button>
        )}
      </div>

      {/* Dropdown select */}
      <select
        id={field.name}
        name={field.name}
        value={selectValue}
        onChange={(e) => {
          const newValue =
            e.target.value === "true" ? true : e.target.value === "false" ? false : null;
          field.handleChange(newValue);
        }}
        onBlur={field.handleBlur}
        disabled={readOnly || field.state.meta.isValidating || field.form.state.isSubmitting}
        className={cn(
          "w-full rounded-lg border border-bordercolor-primary bg-white px-4 py-[13px] text-17 font-medium leading-tight",
          hasValue ? "text-txt-primary" : "text-txt-secondary",
          "focus:border-brand-primary focus:ring-brand-primary focus:outline-none focus:ring-1",
          "disabled:cursor-not-allowed disabled:opacity-50",
          hasError && "border-error-100 focus:border-error-100 focus:ring-error-100",
        )}
      >
        <option value="" disabled>
          Selecteer een optie
        </option>
        <option value="true">Ja</option>
        <option value="false">Nee</option>
      </select>

      {/* Error message */}
      {hasError && (
        <p className="text-sm font-medium leading-[1.4] text-error-120">{errorMessage}</p>
      )}

      {/* Info Modal */}
      {description && (
        <ModalSheetAutoHeight ref={infoModalRef}>
          <div className="flex max-h-[80vh] flex-col gap-5 px-6 pb-6 pt-4">
            {/* Header with title and close */}
            <div className="flex shrink-0 items-center justify-between">
              <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">{label}</h2>
              <button
                type="button"
                onClick={() => infoModalRef.current?.dismiss()}
                className="flex size-12 items-center justify-center"
              >
                <IonIcon icon={closeOutline} className="text-2xl text-txt-primary" />
              </button>
            </div>

            {/* Content - properly handles whitespace with whitespace-pre-line */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <p className="whitespace-pre-line text-md font-medium leading-[1.5] text-txt-tertiary">
                {description}
              </p>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={() => infoModalRef.current?.dismiss()}
              className="w-full shrink-0 rounded-xl bg-button-primary py-3 text-center text-md font-semibold text-txt-inverted"
            >
              Begrepen
            </button>
          </div>
        </ModalSheetAutoHeight>
      )}
    </div>
  );
}
